// CustomCalendar.tsx
import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import CalendarGrid from '../Calendar/CalendarGrid';
import CalendarHeader from '../Calendar/CalendarHeader';
import DateDisplay from '../Calendar/DateDisplay';
import { useCalendarHandlers } from '../Calendar/hooks/useCalendarHandlers';
import { useCalendarState } from '../Calendar/hooks/useCalendarState';
import RangeSelectionControls from '../Calendar/RangeSelectionControls';
import { CalendarProps, getDateFnsLocale } from '../Calendar/types';
import WeekDays from '../Calendar/WeekDays';


const CustomCalendar: React.FC<CalendarProps> = ({
    selectedDate,
    onChange,
    maxDate = new Date(2050, 11, 31),
    minDate = new Date(1900, 0, 1),
    isRangeSelection = false,
    startDate,
    endDate,
    onRangeChange,
    events = [],
    disabledDays = []
}) => {
    const { t, i18n } = useTranslation();
    const calendarRef = useRef<HTMLDivElement>(null);
    const currentLocale = getDateFnsLocale(i18n.language);

    // Use custom hooks for state management and event handlers
    const {
        currentMonth,
        showCalendar,
        rangeStart,
        rangeEnd,
        localSelectedDate,
        hoverDate,
        hasProcessedEvents,
        nextMonth,
        prevMonth,
        openCalendar,
        closeCalendar,
        toggleCalendar,
        setRangeStart,
        setRangeEnd,
        setHoverDate,
        setLocalSelectedDate, 
        eventsByDate
    } = useCalendarState(selectedDate, startDate, endDate, minDate, maxDate, events);

    const {
        handleDateClick,
        handleMouseEnter,
        handleMouseLeave,
        confirmSelection,
        cancelSelection
    } = useCalendarHandlers(
        isRangeSelection,
        rangeStart,
        setRangeStart,
        rangeEnd,
        setRangeEnd,
        localSelectedDate,
        setLocalSelectedDate, // Pass the proper setter function
        setHoverDate,
        minDate,
        maxDate,
        disabledDays,
        onChange,
        onRangeChange,
        closeCalendar
    );

    // Handle clicks outside calendar
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
                closeCalendar();
            }
        };

        if (showCalendar) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCalendar, closeCalendar]);

    // Get weekdays from translations
    const weekDays = [
        t('calendar.weekDays.sunday'),
        t('calendar.weekDays.monday'),
        t('calendar.weekDays.tuesday'),
        t('calendar.weekDays.wednesday'),
        t('calendar.weekDays.thursday'),
        t('calendar.weekDays.friday'),
        t('calendar.weekDays.saturday')
    ];

    // Clase simplificada para el dropdown del calendario
    const calendarDropdownClasses = "absolute z-10 mt-2 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 w-72";

    return (
        <div className="relative" ref={calendarRef}>
            {/* Date Display */}
            <DateDisplay
                isRangeSelection={isRangeSelection}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                selectedDate={localSelectedDate}
                locale={currentLocale}
                hasProcessedEvents={hasProcessedEvents}
                onClick={toggleCalendar}
            />

            {/* Calendar Dropdown */}
            {showCalendar && (
                <div className={calendarDropdownClasses}>
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        </h3>
                        <button
                            onClick={closeCalendar}
                            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                            aria-label="Close calendar"
                        >
                            <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        </button>
                    </div>

                    {/* Calendar Header (Month Navigation) */}
                    <CalendarHeader
                        currentMonth={currentMonth}
                        minDate={minDate}
                        maxDate={maxDate}
                        prevMonth={prevMonth}
                        nextMonth={nextMonth}
                        locale={currentLocale}
                    />

                    {/* Weekday Labels */}
                    <WeekDays weekDays={weekDays} />

                    {/* Calendar Grid */}
                    <CalendarGrid
                        currentMonth={currentMonth}
                        minDate={minDate}
                        maxDate={maxDate}
                        disabledDays={disabledDays}
                        localSelectedDate={localSelectedDate}
                        rangeStart={rangeStart}
                        rangeEnd={rangeEnd}
                        hoverDate={hoverDate}
                        eventsByDate={eventsByDate}
                        onDateClick={handleDateClick}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        locale={currentLocale}
                    />

                    {/* Range Selection Controls */}
                    {isRangeSelection && (
                        <RangeSelectionControls
                            onCancel={() => cancelSelection(startDate, endDate)}
                            onConfirm={confirmSelection}
                            isConfirmDisabled={!rangeStart || !rangeEnd}
                            cancelLabel={t('calendar.cancelButton') || 'Cancel'}
                            confirmLabel={t('calendar.confirmButton') || 'Apply'}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default React.memo(CustomCalendar);